import { reactive } from 'vue';
import PurchaseProjectsAPI from 'dashboard/api/purchaseProjects';

const state = reactive({
  purchaseProjectsByContactId: {},
  loadingByContactId: {},
  loadedByContactId: {},
  errorByContactId: {},
});

const normalizeContactId = contactId => String(contactId ?? '');

const normalizeTimestamp = value => {
  if (!value) return null;
  if (typeof value === 'number') return value;
  return Math.floor(new Date(value).getTime() / 1000);
};

const fromApiPayload = purchaseProject => {
  const metadata = purchaseProject.metadata ?? {};
  const beneficiary = metadata.beneficiary ?? {};
  const items = Array.isArray(metadata.items) ? metadata.items : [];
  const contactId = normalizeContactId(purchaseProject.contact_id);
  const lastMessageSentActivity = [...(metadata.activity ?? [])].find(
    activity => activity.type === 'message_sent' && activity.channel
  );

  return {
    id: purchaseProject.id,
    contactId,
    customerId: contactId,
    createdById: purchaseProject.created_by_id,
    sellerId: metadata.seller_id ?? purchaseProject.created_by_id,
    sellerName: metadata.seller_name ?? purchaseProject.created_by_name ?? '',
    title: purchaseProject.title ?? '',
    description: purchaseProject.description ?? '',
    status: purchaseProject.status ?? 'draft',
    occasion: purchaseProject.occasion ?? '',
    eventDate: purchaseProject.event_date ?? '',
    dueDate: purchaseProject.event_date ?? '',
    source: purchaseProject.source ?? 'dashboard',
    internalNote: purchaseProject.description ?? '',
    beneficiaryType: beneficiary.type ?? 'main_contact',
    beneficiaryId: beneficiary.id ?? null,
    beneficiarySnapshot: beneficiary.snapshot ?? null,
    items,
    itemsCount: purchaseProject.items_count ?? items.length,
    activity: metadata.activity ?? [],
    lastSharedAt: normalizeTimestamp(metadata.last_shared_at),
    lastSharedChannel:
      metadata.last_shared_channel ?? lastMessageSentActivity?.channel ?? null,
    metadata,
    createdAt: normalizeTimestamp(purchaseProject.created_at),
    updatedAt: normalizeTimestamp(purchaseProject.updated_at),
  };
};

const toApiPayload = payload => {
  const metadata = { ...(payload.metadata ?? {}) };

  if (payload.sellerId) metadata.seller_id = payload.sellerId;
  if (payload.sellerName) metadata.seller_name = payload.sellerName;
  if (Array.isArray(payload.activity)) metadata.activity = payload.activity;
  if (Array.isArray(payload.items)) metadata.items = payload.items;
  if (payload.lastSharedAt) metadata.last_shared_at = payload.lastSharedAt;
  if (payload.lastSharedChannel) {
    metadata.last_shared_channel = payload.lastSharedChannel;
  }

  metadata.beneficiary = {
    type: payload.beneficiaryType ?? 'main_contact',
    id: payload.beneficiaryId ?? null,
    snapshot: payload.beneficiarySnapshot ?? null,
  };

  return {
    title: payload.title?.trim() ?? '',
    description: payload.description ?? payload.internalNote ?? '',
    status: payload.status ?? 'draft',
    occasion: payload.occasion || null,
    event_date: payload.eventDate || payload.dueDate || null,
    source: payload.source ?? 'dashboard',
    metadata,
  };
};

const purchaseProjectsForNormalizedContact = contactId =>
  state.purchaseProjectsByContactId[contactId] ?? [];

const setPurchaseProjectsForContact = (contactId, purchaseProjects) => {
  state.purchaseProjectsByContactId[contactId] = purchaseProjects;
};

const upsertById = (contactId, purchaseProject) => {
  const purchaseProjects = purchaseProjectsForNormalizedContact(contactId);
  const index = purchaseProjects.findIndex(
    item => String(item.id) === String(purchaseProject.id)
  );
  const nextPurchaseProjects =
    index === -1
      ? [purchaseProject, ...purchaseProjects]
      : purchaseProjects.map(item =>
          String(item.id) === String(purchaseProject.id)
            ? purchaseProject
            : item
        );

  setPurchaseProjectsForContact(contactId, nextPurchaseProjects);
};

export const usePurchaseProjectsState = () => {
  const purchaseProjectsForContact = contactId => {
    const normalized = normalizeContactId(contactId);
    return purchaseProjectsForNormalizedContact(normalized);
  };

  const findPurchaseProject = purchaseProjectId =>
    Object.values(state.purchaseProjectsByContactId)
      .flat()
      .find(
        purchaseProject =>
          String(purchaseProject.id) === String(purchaseProjectId)
      );

  const loadPurchaseProjects = async (contactId, { force = false } = {}) => {
    const normalized = normalizeContactId(contactId);
    if (!normalized) return [];
    if (!force && state.loadedByContactId[normalized]) {
      return purchaseProjectsForNormalizedContact(normalized);
    }
    if (state.loadingByContactId[normalized]) {
      return purchaseProjectsForNormalizedContact(normalized);
    }

    state.loadingByContactId[normalized] = true;
    state.errorByContactId[normalized] = null;

    try {
      const { data } = await PurchaseProjectsAPI.get(normalized);
      const purchaseProjects = data.map(fromApiPayload);
      setPurchaseProjectsForContact(normalized, purchaseProjects);
      state.loadedByContactId[normalized] = true;
      return purchaseProjects;
    } catch (error) {
      state.errorByContactId[normalized] = error;
      return purchaseProjectsForNormalizedContact(normalized);
    } finally {
      state.loadingByContactId[normalized] = false;
    }
  };

  const createPurchaseProject = async (contactId, payload) => {
    const normalized = normalizeContactId(contactId);
    const { data } = await PurchaseProjectsAPI.create(
      normalized,
      toApiPayload(payload)
    );
    const purchaseProject = fromApiPayload(data);
    upsertById(normalized, purchaseProject);
    return purchaseProject;
  };

  const updatePurchaseProject = async (purchaseProjectId, payload) => {
    const existing = findPurchaseProject(purchaseProjectId);
    if (!existing) return null;
    const normalized = normalizeContactId(existing.contactId);
    const { data } = await PurchaseProjectsAPI.update(
      normalized,
      purchaseProjectId,
      toApiPayload({ ...existing, ...payload })
    );
    const purchaseProject = fromApiPayload(data);
    upsertById(normalized, purchaseProject);
    return purchaseProject;
  };

  const deletePurchaseProject = async purchaseProjectId => {
    const existing = findPurchaseProject(purchaseProjectId);
    if (!existing) return;
    const normalized = normalizeContactId(existing.contactId);
    await PurchaseProjectsAPI.delete(normalized, purchaseProjectId);
    setPurchaseProjectsForContact(
      normalized,
      purchaseProjectsForNormalizedContact(normalized).filter(
        purchaseProject =>
          String(purchaseProject.id) !== String(purchaseProjectId)
      )
    );
  };

  const isLoading = contactId =>
    Boolean(state.loadingByContactId[normalizeContactId(contactId)]);

  const errorForContact = contactId =>
    state.errorByContactId[normalizeContactId(contactId)] ?? null;

  return {
    state,
    purchaseProjectsForContact,
    findPurchaseProject,
    loadPurchaseProjects,
    createPurchaseProject,
    updatePurchaseProject,
    deletePurchaseProject,
    isLoading,
    errorForContact,
  };
};
